import { ArrowUp, CornerDownLeft, Plus, X } from "lucide-react";
import { useState, type KeyboardEvent } from "react";

import { Button } from "../ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

/** An agent a comment can address, e.g. `{ mention: "agent:bear", name: "Bear" }`. */
export interface CommentAgent {
  mention: string;
  name: string;
}

export interface CommentPost {
  body: string;
  mentions: string[];
}

/**
 * A growing comment field. With an `agent`, it starts addressed to that agent
 * (a removable chip, as in Claude's document comments), and the send button
 * says who the comment goes to.
 */
export function CommentComposer({
  placeholder,
  agent,
  autoFocus,
  onPost,
}: {
  placeholder: string;
  agent?: CommentAgent;
  autoFocus?: boolean;
  onPost(post: CommentPost): void;
}) {
  const [body, setBody] = useState("");
  const [addressed, setAddressed] = useState(agent !== undefined);
  const mentions = agent && addressed ? [agent.mention] : [];

  const post = () => {
    if (!body.trim()) return;
    onPost({ body: body.trim(), mentions });
    setBody("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    post();
  };

  return (
    <div className="rounded-md border bg-background focus-within:ring-1 focus-within:ring-ring">
      <textarea
        value={body}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        autoFocus={autoFocus}
        rows={1}
        className="block max-h-40 min-h-9 w-full resize-none bg-transparent px-2.5 py-2 text-sm [field-sizing:content] outline-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:outline-none"
      />
      <div className="flex items-center gap-1 px-1.5 pb-1.5">
        {agent ? (
          addressed ? (
            <span className="inline-flex h-6 items-center gap-0.5 rounded-full bg-primary/10 pr-0.5 pl-2 text-xs font-medium text-primary">
              @{agent.name}
              <button
                type="button"
                aria-label={`Don't send to ${agent.name}`}
                className="rounded-full p-0.5 hover:bg-primary/15"
                onClick={() => setAddressed(false)}
              >
                <X className="size-3" />
              </button>
            </span>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-0.5 px-1.5 text-xs text-muted-foreground"
              title={`Send to ${agent.name}`}
              onClick={() => setAddressed(true)}
            >
              <Plus className="size-3" />
              {agent.name}
            </Button>
          )
        ) : null}
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            {/* A disabled button gets no pointer events; the span still does. */}
            <TooltipTrigger asChild>
              <span className="ml-auto">
                <Button
                  size="icon"
                  className="size-7"
                  aria-label="Send"
                  disabled={!body.trim()}
                  onClick={post}
                >
                  <ArrowUp className="size-4" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent className="flex items-center gap-1.5">
              Send
              <CornerDownLeft className="size-3 opacity-70" />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}
